import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { useColorScheme } from '../../app/color-scheme/ColorSchemeProvider';
import type { PotentialBand } from '../model/automation';
import { DEFAULT_PALETTE, isPalette, type Palette, paletteBandColor } from '../model/palettes';

type PaletteContextValue = {
    readonly bandColor: (band: PotentialBand | undefined) => string;
    readonly colorScheme: 'dark' | 'light';
    readonly palette: Palette;
};

const PaletteContext = createContext<PaletteContextValue | null>(null);

export const PALETTE_SEARCH_PARAM = 'palette';

/** The palette is page state in the URL, so tiles rendered outside the page tree can read the same choice. */
export const usePaletteFromUrl = (): Palette => {
    const [searchParams] = useSearchParams();
    const param = searchParams.get(PALETTE_SEARCH_PARAM);

    return isPalette(param) ? param : DEFAULT_PALETTE;
};

/** Resolves band fills for the whole page from one palette choice, so every chart and chip agrees. */
export const PaletteProvider: FunctionComponent<PropsWithChildren<{ readonly palette: Palette }>> = ({
    children,
    palette,
}) => {
    const { colorScheme } = useColorScheme();
    const value = useMemo<PaletteContextValue>(
        () => ({
            bandColor: (band) =>
                band === undefined ? 'var(--color-border-strong)' : paletteBandColor(palette, colorScheme, band),
            colorScheme,
            palette,
        }),
        [colorScheme, palette],
    );

    return <PaletteContext.Provider value={value}>{children}</PaletteContext.Provider>;
};

export const usePalette = (): PaletteContextValue => {
    const context = useContext(PaletteContext);
    const { colorScheme } = useColorScheme();

    return useMemo<PaletteContextValue>(
        () =>
            context ?? {
                bandColor: (band) =>
                    band === undefined
                        ? 'var(--color-border-strong)'
                        : paletteBandColor(DEFAULT_PALETTE, colorScheme, band),
                colorScheme,
                palette: DEFAULT_PALETTE,
            },
        [colorScheme, context],
    );
};
